import ProductRec from '../../components/ProductRec';
import { constructorParamsBuilder, togglePlaceholder } from '../../core/ConstructorSdk';

class Recommendations extends EventTarget {
  // The Cart's "Recently Viewed" carousel reuses the same markup but has no Constructor pod behind
  // it, so it's excluded here — otherwise it would be queued as a rec that can never resolve, and
  // `processing` would never be released for the pods that follow it.
  #desktopSelector = '.product-rec-l:not(.recently-viewed-l):has(.product-rec-c-carousel[data-desktop="true"])';
  #mobileSelector = '.product-rec-l:not(.recently-viewed-l):has(.product-rec-c-carousel[data-mobile="true"])';

  constructor() {
    super();
    const selector = window.innerWidth > 989 ? this.#desktopSelector : this.#mobileSelector;
    this.recs = document.querySelectorAll(selector);
    this.queue = [];
    this.processing = false;
    this.log = [];
    this.bypassPlaceholder = false;
    this.recCarousels = [];
    this.ignoreQueuesProcessed = false;
  }

  init() {
    // Guard condition, it gracefully exits under this scenario, we will wait for Fast Simon response
    if (
      window.location.pathname === '/pages/search' ||
      (window.location.pathname.includes('/collections') && !window.location.pathname.includes('/products'))
    )
      return;

    this.queue.push({
      id: Date.now(),
      status: 'pending',
      pods: [],
      message: '',
    });

    if (!this.processing) {
      this.process();
    }
  }

  /**
   * Push a new task in the queue
   * @param {Object} param0 - context
   * @param {Boolean} param0.bypassPlaceholder - turn on skipping placeholder
   * @param {Boolean} param0.reset - reset carousel
   * @param {Boolean} param0.ignoredQueuesProcessed - skip previous processed queues
   */
  enqueue({ bypassPlaceholder, reset, ignoreQueuesProcessed } = {}) {
    this.queue.push({
      id: Date.now(),
      status: 'pending',
      pods: [],
      message: '',
    });

    // Set true if you want to skip toggling placeholder UI process
    if (bypassPlaceholder) {
      this.bypassPlaceholder = true;
    }

    // Set true if you want to skip previous processed queues
    if (ignoreQueuesProcessed) {
      this.ignoreQueuesProcessed = true;
    }

    if (reset) {
      // come back to zero the carousel scrolling
      this.recCarousels.forEach((recCarousel) => recCarousel.reset());
    }

    if (!this.processing) {
      this.process();
    }
  }

  process() {
    if (this.queue.length === 0) {
      this.processing = false;
      return;
    }

    // Start processing and takes the first element in the queue
    this.processing = true;
    const item = this.queue.shift();

    // It gracefully exists if there are no rec containers
    if (!this.recs && !this.recs.length) {
      this.#writeLog(item, { status: 'failed', message: 'there were no recs' });
      return this.process();
    }
    // Given that there are existing product recs in the page we should proceed to make network fetch to Constructor API to get results.
    // You could bring up the fetch or Constructor API class/interface to get the response
    // Then, we will use this data to populate carousels and product card. See next commend down below.
    const podIdAttribute = 'data-cnstrc-recommendations-pod-id';

    this.recs.forEach((rec, idx) => {
      const podIdElement = rec.querySelector(`[${podIdAttribute}]`);

      if (!podIdElement) {
        console.error('pod element not found');
        return;
      }

      const podId = podIdElement.getAttribute(podIdAttribute);
      if (!podId) {
        console.error('pod id not found');
        return;
      }

      // Check if that podId was processed successfully to prevent to make the same newtork fetch twice
      if (
        !this.ignoreQueuesProcessed &&
        this.log.some((item) => item.status === 'success' && item.pods.includes(podId))
      ) {
        this.processing = false;
        return;
      }

      // Collect product IDs from the page
      const productElements = document.querySelectorAll('[data-product-id]:not([data-cnstrc-item="recommendation"])');
      const itemIds = [
        ...new Set(
          Array.from(productElements)
            .map((el) => el.getAttribute('data-product-id'))
            .filter((id) => id),
        ),
      ];

      // Populate seed items
      itemIds.length && podIdElement.setAttribute('data-cnstrc-recommendations-seed-items', itemIds.join(','));

      // Only include itemIds parameter if we found product IDs on the page
      const params = itemIds.length > 0 ? { itemIds } : {};
      constructorParamsBuilder(params, true);

      // Number of results that will be fetched from API call.
      const numResults = Number(rec.querySelector('[data-total-items]')?.dataset?.totalItems) || 10;
      params.numResults = numResults;

      // If on search page (indicated by Liquid template), extract query from URL
      const isSearchPage = podIdElement.hasAttribute('data-is-search-page');
      if (isSearchPage) {
        const urlParams = new URLSearchParams(window.location.search);
        let searchQuery = urlParams.get('q');
        if (searchQuery) {
          // Strip HTML tags and trim whitespace
          searchQuery = searchQuery.replace(/<[^>]*>?/gm, '');
          searchQuery = searchQuery.trim();
          // whenever this term exceeds 100 characters, it extracts this length.
          // Constructor API cannot stand more than 100 characters
          searchQuery = searchQuery.length > 100 ? searchQuery.slice(0, 100) : searchQuery;
          params.term = searchQuery;
        }
      }

      window.cnstrcSdk.recommendations
        .getRecommendations(podId, params)
        .then((response) => {
          // Update Recommendation title whenever it was empty
          const recTitle = rec.querySelector('h2.product-rec-c-heading');
          if (!recTitle.textContent) {
            recTitle.textContent = response?.response?.pod.display_name;
          }

          // Extract metadata from API response for Constructor tracking
          const resultId = response?.result_id || '';
          const results = response?.response?.results || [];
          // total_num_results may be at top level or nested in response
          const numResults = response?.total_num_results || response?.response?.total_num_results || results.length;

          // Store and initialize carousel after data is populated
          const productRecInstance = new ProductRec({
            gallery: rec,
            prefixClassName: 'carousel-c-scroll',
            gap: 8,
            recData: results,
            resultId,
            numResults,
            podId,
          });
          this.recCarousels.push(productRecInstance);
          productRecInstance.init();

          // Gets rid of placeholder ui on rec only if it wasn't processed before
          if (!this.log.some((item) => item.status === 'success' && item.pods.includes(podId))) {
            !this.bypassPlaceholder && togglePlaceholder(rec);
          }

          this.#writeLog(item, { status: 'success', podId, message: `${podId} was processed,` });
          if (this.recs.length === idx + 1) {
            this.processing = false;
          }
        })
        .catch((error) => {
          const message = `Constructor Error for ${podId}: ${error}`;
          console.error(message);
          this.#writeLog(item, { status: 'failed', podId, message });
          return this.process();
        });
    });
  }

  /**
   * Write a Recommendation log per each rec container
   * @param {Object} item - log item
   * @param {String} param1.status - item status
   * @param {String} param1.message - item message
   * @param {String} param1.podId - item podId
   */
  #writeLog(item, { status, message, podId }) {
    const existingEntry = this.log.find((entry) => entry.id === item.id);
    if (existingEntry) {
      existingEntry.status = status;
      if (message) {
        existingEntry.message += message;
      }
      if (podId) {
        existingEntry.pods.push(podId);
      }
      return;
    }

    item.status = status;
    if (message) {
      item.message += message;
    }
    if (podId) {
      item.pods.push(podId);
    }
    return this.log.push(item);
  }
}

// Make it global through window object
if (typeof window !== 'undefined') {
  window.recommendations = new Recommendations();
}
